import { ApplicationLayout } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import type { PolicyholderPage, PolicyholderPageType } from 'korint-sdk/react';
import { CreditCardIcon, FilesIcon, HomeIcon, ReceiptEuroIcon, UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function getDefaultPolicyholderPageIcon(type: PolicyholderPageType): React.ReactNode {
  switch (type) {
    case 'home':
      return <HomeIcon className="size-4" />;
    case 'information':
      return <UserIcon className="size-4" />;
    case 'documents':
      return <FilesIcon className="size-4" />;
    case 'payment-methods':
      return <CreditCardIcon className="size-4" />;
    case 'invoices':
      return <ReceiptEuroIcon className="size-4" />;
  }
}

const POLICYHOLDER_PAGE_DEFAULT_LABEL_KEY: Record<PolicyholderPageType, string> = {
  home: 'sidebar.policyholder.home',
  information: 'sidebar.policyholder.information',
  documents: 'sidebar.policyholder.documents',
  'payment-methods': 'sidebar.policyholder.paymentMethods',
  invoices: 'sidebar.policyholder.invoices',
};

export function getPolicyholderPageLabel(
  page: PolicyholderPage,
  t: (key: string) => string,
): string {
  if (page.type === 'custom') return t(`sidebar.policyholder.${page.name}`);
  return t(POLICYHOLDER_PAGE_DEFAULT_LABEL_KEY[page.type]);
}

export function PolicyholderDashboardLayout({
  pages,
  logo,
}: {
  pages: ReadonlyArray<PolicyholderPage>;
  logo: React.ReactNode;
}) {
  const { policyId, branchId } = useAssertedParams();
  const { t } = useTranslation();

  const items = pages.map(page => {
    if (page.type === 'custom') {
      return {
        label: getPolicyholderPageLabel(page, t),
        link: branchId
          ? `/policyholder/${policyId}/${branchId}${page.path}`
          : `/policyholder/${policyId}${page.path}`,
        icon: page.icon,
      };
    }

    const path = page.type === 'home' ? '' : `/${page.type}`;

    return {
      label: getPolicyholderPageLabel(page, t),
      link: branchId
        ? `/policyholder/${policyId}/${branchId}${path}`
        : `/policyholder/${policyId}${path}`,
      icon: getDefaultPolicyholderPageIcon(page.type),
    };
  });

  return <ApplicationLayout items={items} logo={<div>{logo}</div>} />;
}
